<script setup lang="ts">
import { storeToRefs } from "pinia";
import Accordion from "~/components/Common/Accordion.vue";
import AiFieldMarker from "~/components/Pages/company-catalog/training-modal/prefill/AiFieldMarker.vue";
import AiFilledField from "~/components/Pages/company-catalog/training-modal/prefill/AiFilledField.vue";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import PriceInput from "~/components/Common/Inputs/PriceInput.vue";
import RadioInput from "~/components/Common/Inputs/RadioInput.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { TrainingPrefillFieldEnum } from "~/utils/enums/training-prefill.enums";
import { SessionItemModalityEnum, SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import Tooltip from "~/components/Common/Tooltip.vue";
import CheckboxInput from "~/components/Common/Inputs/CheckboxInput.vue";
import { useCurrency } from "~/composables/currency/useCurrency";
import { useCompaniesStore } from "~/store/companies/companies.store";
import { followsDoceboRules, overbookingNotAllowedFor } from "~/utils/helpers/sessions/session-capacity";
import type { ISessionItemCreateTraining } from "~/utils/interfaces/trainings-interface";

const { currencySymbol } = useCurrency();

const prefillStore = useTrainingPrefillStore();
const createTrainingStore = useCreateTrainingStore();
const { training, readonly } = storeToRefs(createTrainingStore);
const companiesStore = useCompaniesStore();

const PRICE_FIELD_OF: Record<SessionItemTypeEnum, TrainingPrefillFieldEnum> = {
  [SessionItemTypeEnum.INDIVIDUAL]: TrainingPrefillFieldEnum.PRICE_INTER_EXCL_TAX,
  [SessionItemTypeEnum.GROUP]: TrainingPrefillFieldEnum.PRICE_INTRA_EXCL_TAX,
};

const followsDocebo = computed(() => followsDoceboRules(companiesStore.lmsProvider?.lmsProvider));

// The flag is watched too: an item reloaded or reset with it off gets Docebo's value back.
function syncOverbookingNotAllowed(sessionItem: () => ISessionItemCreateTraining) {
  watch(
    [() => sessionItem().maxNbAttendees, () => sessionItem().overbookingNotAllowed, followsDocebo],
    () => {
      const item = sessionItem();
      item.overbookingNotAllowed = overbookingNotAllowedFor(
        item.overbookingNotAllowed,
        item.maxNbAttendees,
        followsDocebo.value,
      );
    },
    { immediate: true },
  );
}

syncOverbookingNotAllowed(() => training.value.sessionItems.individual);
syncOverbookingNotAllowed(() => training.value.sessionItems.group);
</script>
<template>
  <h3 class="heading_3_demi">Inscriptions</h3>
  <div class="mt-4 px-4">
    <Accordion
      v-for="sessionItem in training.sessionItems"
      v-if="training.general.type === TrainingTypeEnum.EXTERNAL"
      v-model="sessionItem.isChecked"
      :disabled="readonly"
      class="mb-8"
    >
      <template #headers>
        <div class="flex flex-col">
          <div v-if="sessionItem.type === SessionItemTypeEnum.INDIVIDUAL">
            <SvgIcon class="w-6 h-6" name="indiv"></SvgIcon>
            <h5 class="body_demi">Inscription individualisée - Inter</h5>
            <span class="text-secondary-color">
              Vous réserverez une place par participant et chaque inscription sera traitée individuellement, que le
              cours soit en groupe ou individuel.</span
            >
          </div>

          <div v-else>
            <SvgIcon class="w-6 h-6" name="group"></SvgIcon>
            <h5 class="body_demi">Inscription d’un groupe - Intra</h5>
            <span class="text-secondary-color">
              Vous réservez une session pour un groupe de participants. Ex : une session à 3 000
              {{ currencySymbol }} pour un maximum de 10 participants.</span
            >
          </div>
        </div>
      </template>
      <template #body>
        <div v-if="sessionItem.type === SessionItemTypeEnum.INDIVIDUAL" class="fields">
          <span class="text-secondary-color">La formation a-t-elle des dates pré-définies ? *</span>
          <div v-for="item in SessionItemModalityEnum" :key="item" class="text-secondary-color">
            <label class="flex w-fit gap-2" :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'">
              <RadioInput
                :disabled="readonly"
                :checked="sessionItem.modality === item"
                @update:model-value="sessionItem.modality = item"
              >
              </RadioInput>
              <span v-if="item === SessionItemModalityEnum.ON_DEMAND" class="text-main-color"
                >Non, chaque participant peut démarrer dès son inscription</span
              >
              <span v-else class="text-main-color"
                >Oui, la formation a des sessions et le participant doit attendre cette date pour démarrer</span
              >
            </label>
          </div>
        </div>
        <div class="fields">
          <span class="text-secondary-color !flex flex-row items-center gap-2">
            Prix *
            <AiFieldMarker
              v-if="
                sessionItem.type && prefillStore.isPrefilled(PRICE_FIELD_OF[sessionItem.type as SessionItemTypeEnum])
              "
            />
          </span>
          <AiFilledField
            :active="
              !!sessionItem.type && prefillStore.isPrefilled(PRICE_FIELD_OF[sessionItem.type as SessionItemTypeEnum])
            "
          >
            <div class="flex items-center gap-2">
              <div class="w-[88px]">
                <PriceInput
                  v-model="sessionItem.priceExclTax"
                  :disabled="readonly"
                  type="number"
                  placeholder="-"
                  :error="
                    !!(
                      training.errors &&
                      training.errors['sessionItems.individual.priceExclTax'] &&
                      sessionItem.type === SessionItemTypeEnum.INDIVIDUAL
                    ) ||
                    !!(
                      training.errors &&
                      training.errors['sessionItems.group.priceExclTax'] &&
                      sessionItem.type === SessionItemTypeEnum.GROUP
                    )
                  "
                ></PriceInput>
              </div>
              <span v-if="sessionItem.type === SessionItemTypeEnum.INDIVIDUAL" class="text-secondary-color"
                >/ place (HT)</span
              >
              <span v-else class="text-secondary-color ml-2">/ groupe (HT)</span>
            </div>
          </AiFilledField>
          <span
            v-if="
              training.errors &&
              training.errors['sessionItems.group.priceExclTax'] &&
              sessionItem.type === SessionItemTypeEnum.GROUP
            "
            class="text-primary-red-color"
            >{{ training.errors["sessionItems.group.priceExclTax"] }}</span
          >
          <span
            v-else-if="
              training.errors &&
              training.errors['sessionItems.individual.priceExclTax'] &&
              sessionItem.type === SessionItemTypeEnum.INDIVIDUAL
            "
            class="text-primary-red-color"
            >{{ training.errors["sessionItems.individual.priceExclTax"] }}</span
          >
        </div>
        <div class="fields !mb-0">
          <div class="flex gap-1 items-center">
            <span class="text-secondary-color">Nombre de places</span>
            <Tooltip :position="'top-right'" class="ml-1">
              <template #content>
                <div class="flex w-[414px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                  <span class="font-medium text_kpi"
                    >Si vous ne connaissez pas le nombre de places max alors vous pouvez mettre un chiffre
                    arbitraire.</span
                  >
                </div>
              </template>
            </Tooltip>
          </div>
          <div class="flex gap-4">
            <div class="flex flex-1 flex-col gap-2">
              <div class="flex gap-2 items-center">
                <div class="w-[88px]">
                  <CommonInput
                    v-model="sessionItem.minNbAttendees"
                    :nullable="true"
                    :disabled="readonly"
                    type="number"
                    placeholder="-"
                    :error="
                      !!(
                        (training.errors &&
                          training.errors['sessionItems.group.minNbAttendees'] &&
                          sessionItem.type === SessionItemTypeEnum.GROUP) ||
                        (training.errors['sessionItems.individual.minNbAttendees'] &&
                          sessionItem.type === SessionItemTypeEnum.INDIVIDUAL)
                      )
                    "
                  ></CommonInput>
                </div>
                <span class="text-secondary-color">min</span>
              </div>
              <span
                v-if="
                  (training.errors &&
                    training.errors['sessionItems.group.minNbAttendees'] &&
                    sessionItem.type === SessionItemTypeEnum.GROUP) ||
                  (training.errors['sessionItems.individual.minNbAttendees'] &&
                    sessionItem.type === SessionItemTypeEnum.INDIVIDUAL)
                "
                class="text-primary-red-color"
                >{{
                  training.errors["sessionItems.group.minNbAttendees"] ||
                  training.errors["sessionItems.individual.minNbAttendees"]
                }}</span
              >
            </div>
            <div class="flex flex-1 flex-col gap-2">
              <div class="flex gap-2 items-center">
                <div class="w-[88px]">
                  <CommonInput
                    v-model="sessionItem.maxNbAttendees"
                    :nullable="true"
                    :disabled="readonly"
                    type="number"
                    placeholder="-"
                    :error="
                      !!(
                        (training.errors &&
                          training.errors['sessionItems.group.maxNbAttendees'] &&
                          sessionItem.type === SessionItemTypeEnum.GROUP) ||
                        (training.errors['sessionItems.individual.maxNbAttendees'] &&
                          sessionItem.type === SessionItemTypeEnum.INDIVIDUAL)
                      )
                    "
                  ></CommonInput>
                </div>
                <span class="text-secondary-color"
                  >max {{ sessionItem.type === SessionItemTypeEnum.GROUP ? "*" : "" }}</span
                >
              </div>
              <span
                v-if="
                  training.errors &&
                  training.errors['sessionItems.group.maxNbAttendees'] &&
                  sessionItem.type === SessionItemTypeEnum.GROUP
                "
                class="text-primary-red-color"
                >{{ training.errors["sessionItems.group.maxNbAttendees"] }}</span
              >
            </div>
          </div>
        </div>
        <div v-if="!followsDocebo" class="flex items-center gap-2 mt-3">
          <label
            class="flex items-center gap-2"
            :class="!readonly && Number.isFinite(sessionItem.maxNbAttendees) ? 'cursor-pointer' : 'cursor-not-allowed'"
          >
            <CheckboxInput
              v-if="Number.isFinite(sessionItem.maxNbAttendees)"
              v-model:model-value="sessionItem.overbookingNotAllowed"
              :disabled="readonly"
              :checked="sessionItem.overbookingNotAllowed"
            ></CheckboxInput>
            <Tooltip v-else :position="'top-right'">
              <template #img>
                <CheckboxInput
                  v-model:model-value="sessionItem.overbookingNotAllowed"
                  :disabled="!Number.isFinite(sessionItem.maxNbAttendees)"
                  :checked="sessionItem.overbookingNotAllowed"
                ></CheckboxInput>
              </template>
              <template #content>
                <div class="flex w-[414px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                  <span class="font-medium text_kpi"
                    >Pour bloquer les inscriptions vous devez saisir un nombre de place maximal.</span
                  >
                </div>
              </template>
            </Tooltip>
            <span class="text-secondary-color">Bloquer les inscriptions une fois la capacité max atteinte</span>
          </label>
          <Tooltip :position="'top-right'" class="ml-1">
            <template #content>
              <div class="flex w-[414px] gap-[8px]">
                <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                <span class="font-medium text_kpi"
                  >Le nombre de places est modifiable sur chaque session. Si vous cochez cette case, une fois le nombre
                  de place max atteint les participants ne pourront plus s’inscrire mais ils pourront être ajoutés par
                  un Admin ou un Référent.</span
                >
              </div>
            </template>
          </Tooltip>
        </div>
      </template>
    </Accordion>
    <Accordion v-else v-model="training.sessionItems.individual.isChecked" class="mb-8">
      <template #headers>
        <div class="flex flex-col">
          <div>
            <SvgIcon class="w-6 h-6 text-secondary-color" name="graduation_cap_on_v2"></SvgIcon>
            <h5 class="body_demi">Interne</h5>
            <span class="text-secondary-color">
              Vous êtes en train de créer une formation interne, dont le format et le nombre de participants sont à
              préciser. Ex: un cours en ligne avec un formateur interne pour 10 personnes.</span
            >
          </div>
        </div>
      </template>
      <template #body>
        <div class="fields">
          <span class="text-secondary-color">La formation a-t-elle des dates pré-définies ? *</span>
          <div v-for="item in SessionItemModalityEnum" :key="item" class="text-secondary-color">
            <label class="flex w-fit gap-2" :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'">
              <RadioInput
                :disabled="readonly"
                :checked="training.sessionItems.individual.modality === item"
                @update:model-value="training.sessionItems.individual.modality = item"
              ></RadioInput>
              <span v-if="item === SessionItemModalityEnum.ON_DEMAND" class="text-main-color"
                >Non, chaque participant peut démarrer dès son inscription</span
              >
              <span v-else class="text-main-color"
                >Oui, la formation a des sessions et le participant doit attendre cette date pour démarrer</span
              >
            </label>
          </div>
        </div>
        <div class="fields">
          <span class="text-secondary-color">Prix *</span>
          <div class="flex items-center gap-2">
            <div class="w-[88px]">
              <PriceInput
                v-model="training.sessionItems.individual.priceExclTax"
                :disabled="readonly"
                type="number"
                placeholder="-"
                :error="!!(training.errors && training.errors['sessionItems.individual.priceExclTax'])"
              ></PriceInput>
            </div>
            <span class="text-secondary-color">/ place (HT)</span>
          </div>

          <span
            v-if="training.errors && training.errors['sessionItems.individual.priceExclTax']"
            class="text-primary-red-color"
            >{{ training.errors["sessionItems.individual.priceExclTax"] }}</span
          >
        </div>
        <div class="fields !mb-0">
          <div class="flex gap-1 items-center">
            <span class="text-secondary-color">Nombre de places</span>
            <Tooltip :position="'top-right'" class="ml-1">
              <template #content>
                <div class="flex w-[414px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                  <span class="font-medium text_kpi"
                    >Si vous ne connaissez pas le nombre de places max alors vous pouvez mettre un chiffre
                    arbitraire.</span
                  >
                </div>
              </template>
            </Tooltip>
          </div>
          <div class="flex gap-4">
            <div class="flex flex-1 flex-col gap-2">
              <div class="flex gap-2 items-center">
                <div class="w-[88px]">
                  <CommonInput
                    v-model="training.sessionItems.individual.minNbAttendees"
                    :nullable="true"
                    :disabled="readonly"
                    type="number"
                    placeholder="-"
                    :error="!!training.errors['sessionItems.individual.minNbAttendees']"
                  ></CommonInput>
                </div>
                <span class="text-secondary-color">min</span>
              </div>
              <span
                v-if="training.errors && training.errors['sessionItems.individual.minNbAttendees']"
                class="text-primary-red-color"
                >{{ training.errors["sessionItems.individual.minNbAttendees"] }}</span
              >
            </div>
            <div class="flex flex-1 flex-col gap-2">
              <div class="flex gap-2 items-center">
                <div class="w-[88px]">
                  <CommonInput
                    v-model="training.sessionItems.individual.maxNbAttendees"
                    :nullable="true"
                    :disabled="readonly"
                    type="number"
                    placeholder="-"
                    :error="!!(training.errors && training.errors['sessionItems.individual.maxNbAttendees'])"
                  ></CommonInput>
                </div>
                <span class="text-secondary-color">max</span>
              </div>
              <span
                v-if="training.errors && training.errors['sessionItems.individual.maxNbAttendees']"
                class="text-primary-red-color"
                >{{ training.errors["sessionItems.individual.maxNbAttendees"] }}</span
              >
            </div>
          </div>
        </div>
        <div v-if="!followsDocebo" class="flex items-center gap-2 mt-3">
          <label
            class="flex items-center gap-2"
            :class="
              !readonly && Number.isFinite(training.sessionItems.individual.maxNbAttendees)
                ? 'cursor-pointer'
                : 'cursor-not-allowed'
            "
          >
            <CheckboxInput
              v-if="Number.isFinite(training.sessionItems.individual.maxNbAttendees)"
              v-model:model-value="training.sessionItems.individual.overbookingNotAllowed"
              :disabled="readonly"
              :checked="training.sessionItems.individual.overbookingNotAllowed"
            ></CheckboxInput>
            <Tooltip v-else :position="'top-right'">
              <template #img>
                <CheckboxInput
                  v-model:model-value="training.sessionItems.individual.overbookingNotAllowed"
                  :disabled="!Number.isFinite(training.sessionItems.individual.maxNbAttendees)"
                  :checked="training.sessionItems.individual.overbookingNotAllowed"
                ></CheckboxInput>
              </template>
              <template #content>
                <div class="flex w-[414px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                  <span class="font-medium text_kpi"
                    >Pour bloquer les inscriptions vous devez saisir un nombre de place maximal.</span
                  >
                </div>
              </template>
            </Tooltip>
            <span class="text-secondary-color">Bloquer les inscriptions une fois la capacité max atteinte</span>
          </label>
          <Tooltip :position="'top-right'" class="ml-1">
            <template #content>
              <div class="flex w-[414px] gap-[8px]">
                <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                <span class="font-medium text_kpi"
                  >Le nombre de places est modifiable sur chaque session. Si vous cochez cette case, une fois le nombre
                  de place max atteint les participants ne pourront plus s’inscrire mais ils pourront être ajoutés par
                  un Admin ou un Référent.</span
                >
              </div>
            </template>
          </Tooltip>
        </div>
      </template>
    </Accordion>
    <span v-if="training.errors && training.errors['sessionItems']" class="text-primary-red-color">{{
      training.errors["sessionItems"]
    }}</span>
  </div>
</template>
<style scoped lang="scss">
.fields {
  @apply flex flex-col mb-4 gap-2;
}
</style>
